import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useLocalization } from "@fluent/react";

import { ContentCard, DateInput } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { DateTime } from "luxon";
import ApiHttp from "byzantine/src/ApiHttp";
import { modules } from "byzantine";
import DrawerLayout from "../../../../../DrawerLayout";
import MultiCountryPicker from "../../../MultiCountryPicker";
import BackButton from "../../../../../../src/modules/transfers/wire/templates/TemplatesList/BackButton";
import styles from "./TravelNoticeForm.module.scss";

import * as formAdapters from "../../../../../../src/adapters";

type TravelNoticeFormProps = {
  onUserDismiss: () => void;
  goBack: () => void;
  cardId: API.CardId;
}

type DateInputWrapperProps = {
  minDate: string;
  label: string;
  disableDates: ((date: Date) => boolean)[];
  onChange: (value: string | number | boolean) => void;
  id: string;
  onBlur: (e: React.FocusEvent<HTMLElement>) => void;
  error?: string;
  field?: string;
  dateValueRef: React.RefObject<string>;
}

// Memoized wrapper around DateInput
const DateInputWrapper = memo(
  ({
    minDate,
    label,
    disableDates,
    onChange,
    id,
    onBlur,
    error,
    field,
    dateValueRef,
  }: DateInputWrapperProps) => (
    <DateInput
      minDate={minDate}
      label={label}
      dateFormat={"m/d/Y"}
      altInput
      altFormat={"m/d/Y"}
      useIsoOnChange={false}
      disableDates={disableDates}
      endIcon="calendar"
      onChange={onChange}
      id={id}
      onBlur={onBlur}
      error={error}
      field={field}
      defaultDate={dateValueRef.current || undefined}
    />
  )
);

const TravelNoticeForm = ({
  onUserDismiss,
  goBack,
  cardId,
}: TravelNoticeFormProps) => {
  const { l10n } = useLocalization();
  const { form, onValidate } = modules.travelNotes.useCreateTravelNoteForm();
  const [saveIsEnabled, setSaveIsEnabled] = useState(true);
  const { sendNotification } = useNotificationContext();

  const formRef = useRef(form);
  formRef.current = form;

  const startDateRef = useRef<string>("");
  const endDateRef = useRef<string>("");

  const addTravelNote = useCallback(async () => {
    setSaveIsEnabled(false);

    if (!(await onValidate())) {
      setSaveIsEnabled(true);
      return;
    }

    const payload = {
      start_date: DateTime.fromFormat(
        form.values.startDate!,
        "M/d/yyyy"
      ).toFormat("yyyy-MM-dd"),
      end_date: DateTime.fromFormat(form.values.endDate!, "M/d/yyyy").toFormat(
        "yyyy-MM-dd"
      ),
      travel_destination_codes: form.values.travelDestinationCodes!,
    };
    try {
      await ApiHttp.fetch(
        `cards/${cardId}/travel_note/`,
        {
          method: "POST",
        },
        payload
      );
      sendNotification({
        type: "success",
        text: l10n.getString("travel-notice-added-success"),
      });
      onUserDismiss();
    } catch (error) {
      sendNotification({
        type: "negative",
        text: l10n.getString("travel-notice-added-failure"),
      });
    } finally {
      setSaveIsEnabled(true);
    }
  }, [
    form.values.endDate,
    form.values.startDate,
    form.values.travelDestinationCodes,
    onValidate,
  ]);

  const disableStartDates = useMemo(
    () => [
      (date: Date) => {
        if (!form.values.endDate) return false;
        return (
          DateTime.fromJSDate(date) >
          DateTime.fromFormat(form.values.endDate, "M/d/yyyy")
        );
      },
    ],
    [form.values.endDate]
  );

  const disableEndDates = useMemo(
    () => [
      (date: Date) => {
        if (!form.values.startDate) return false;
        return (
          DateTime.fromJSDate(date) <
          DateTime.fromFormat(form.values.startDate, "M/d/yyyy")
        );
      },
    ],
    [form.values.startDate]
  );

  const todayStr = useMemo(() => DateTime.now().toFormat("MM/dd/yyyy"), []);

  const onStartDateChange = useCallback((value: string | number | boolean) => {
    startDateRef.current = value as string;
    formRef.current.setFieldValue("startDate", value, true);
  }, []);
  const onEndDateChange = useCallback((value: string | number | boolean) => {
    endDateRef.current = value as string;
    formRef.current.setFieldValue("endDate", value, true);
  }, []);

  const onDateBlur = useCallback((e: React.FocusEvent<HTMLElement>) => {
    formRef.current.handleBlur(e);
  }, []);

  const startDateError = formAdapters.fieldWithOnChange(
    form,
    "startDate"
  ).error;
  const endDateError = formAdapters.fieldWithOnChange(form, "endDate").error;

  return (
    <DrawerLayout
      onSave={addTravelNote}
      saveLabel={l10n.getString("travel-notice-submit-button")}
      onCancel={onUserDismiss}
      cancelLabel={l10n.getString("travel-notice-cancel-button")}
      isSaveDisabled={!saveIsEnabled}
    >
      <div className={styles.container}>
        <BackButton
          text={l10n.getString("travel-notice-back-button")}
          className="margin--bottom--l"
          onClick={goBack}
        />
        <h2 className={styles.header}>
          {l10n.getString("travel-notice-form-title")}
        </h2>
        <div className="margin--top--xs margin--bottom--xl">
          {l10n.getString("travel-notice-form-description")}
        </div>
        <ContentCard kind="bordered">
          <div className="fontColor--heading fontWeight--bold margin--bottom--l">
            {l10n.getString("travel-notice-form-destination-section")}
          </div>
          <MultiCountryPicker
            label={l10n.getString("travel-notice-country-picker")}
            {...formAdapters.fieldWithOnChange(form, "travelDestinationCodes")}
          />
          <div
            className="margin--y--l border--top"
            style={{
              width: "calc(var(--space-l) + 100% + var(--space-l))",
              marginLeft: "calc(-1*var(--space-l))",
            }}
          />
          <div className="fontColor--heading fontWeight--bold margin--bottom--l">
            {l10n.getString("travel-notice-form-dates-section")}
          </div>
          <DateInputWrapper
            minDate={todayStr}
            label={l10n.getString("travel-notice-start-date-input")}
            disableDates={disableStartDates}
            onChange={onStartDateChange}
            id="startDate"
            onBlur={onDateBlur}
            error={startDateError}
            dateValueRef={startDateRef}
          />
          <div className="margin--bottom--l" />
          <DateInputWrapper
            minDate={form.values.startDate || todayStr}
            field="end_date"
            label={l10n.getString("travel-notice-end-date-input")}
            disableDates={disableEndDates}
            onChange={onEndDateChange}
            id="endDate"
            onBlur={onDateBlur}
            error={endDateError}
            dateValueRef={endDateRef}
          />
        </ContentCard>
      </div>
    </DrawerLayout>
  );
};

export default TravelNoticeForm;
